import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, tap, withLatestFrom, catchError } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import * as fromActions from './actions';
import { Store } from '@ngrx/store';
import { GetMaintenanceListRequest, MaintenanceService } from '@services/api/api.service';
import { Sort } from '@angular/material/sort';
import { selectMaintenances } from '@redux/modules/maintenance/index';

@Injectable()
export class MaintenanceEffects {
  private pageSize: number;
  private pageIndex: number;
  private filters: any;
  private sort: Sort;

  setMaintenancesFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setMaintenancesFilters),
    tap(() => {
      this.store.dispatch(fromActions.setMaintenancesPaginator({ paginator: { page: 1, pageSize: 10 } }));
    }),
  ), { dispatch: false });

  // Load Maintenances
  getMaintenancesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.getMaintenancesList,
        fromActions.setMaintenancesPaginator),
      withLatestFrom(
        this.store.select(selectMaintenances),
      ),
      tap(() => this.store.dispatch(fromActions.setMaintenancesLoading({ isLoading: true }))),
      switchMap(async ([action, maintenances]) => {
        const request: GetMaintenanceListRequest = {
          page: maintenances.paginator.page,
          pageSize: maintenances.paginator.pageSize,
          ...(maintenances.filters.qSearch ? { qSearch: maintenances.filters.qSearch } : {}),
          ...(maintenances.filters.filterModel ? { filterModel: maintenances.filters.filterModel } : {}),
          ...(maintenances.filters.sortModel ? { sortModel: maintenances.filters.sortModel } : {})
        }
        const vehiclesData = await firstValueFrom(this.maintenanceService.getMaintenancesList(request
        ));
        return [
          fromActions.setMaintenancesList({ response: vehiclesData }),
          fromActions.setMaintenancesLoading({ isLoading: false }),
        ];
      }),
      switchMap((actions) => [...actions]),
    ),
  );

  public getData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.getMaintenanceList),
      tap(action => {
        this.pageIndex = action.pageIndex;
        this.pageSize = action.pageSize;
        this.sort = action.sort;
      }),
      tap(() => this.store.dispatch(fromActions.setMaintenanceLoading({ value: true }))),
      switchMap(async () => {
        const { q, ...filterModel } = this.filters || {};
        const vehiclesData = await firstValueFrom(this.maintenanceService
          .getPagedMaintenancesList({
            startRow: (this.pageIndex - 1) * this.pageSize,
            endRow: this.pageIndex * this.pageSize - 1,
            qSearch: q,
            filterModel: {
              ...filterModel,
            },
            ...(this.sort ? {
              sortModel: [
                {
                  colId: this.sort.active,
                  sort: this.sort.direction,
                },
              ],

            } : {}),
          }));

        return [fromActions.setMaintenanceList({
          vehicles: vehiclesData.data,
          page: vehiclesData.currentPage,
          pageSize: this.pageSize,
          totalNumber: vehiclesData.totalCount,
        })];
      }),
      switchMap((actions) => [...actions, fromActions.setMaintenanceLoading({ value: false })]),
    );
  });
  public $setFilters = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromActions.setMaintenanceFilters),
      tap(action => {
        this.filters = action.filters;
      }),
      switchMap(() => [fromActions.getMaintenanceList({
        pageIndex: 1,
        pageSize: this.pageSize,
      }), fromActions.getMaintenanceSummary()]),
    );
  });
  public $getSummary = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getMaintenanceSummary, fromActions.getMaintenancesCounters),
    tap(() => this.store.dispatch(fromActions.setMaintenanceLoading({ value: true }))),
    switchMap(async () => {
      const {
        totalVehicles,
        expiringMaintenance,
        expiredMaintenance,
      } = await firstValueFrom(this.maintenanceService.getMaintenancesCounters());
      return [fromActions.setMaintenanceSummary({
        value: {
          totalVehicles,
          expiringMaintenance,
          expiredMaintenance,
        },
      })];
    }),
    catchError(() => of([fromActions.setMaintenanceSummary({
      value: {
        totalVehicles: 0,
        expiringMaintenance: 0,
        expiredMaintenance: 0,
      },
    })])),
    switchMap((actions) => [...actions]),
  ));

  constructor(
    private actions$: Actions,
    private store: Store,
    private maintenanceService: MaintenanceService,
  ) {
    this.pageIndex = undefined;
    this.pageSize = undefined;
  }
}
